import { useState } from 'react'
import { getStoredSiteKey, setStoredSiteKey, validateSiteKey } from '../siteAuth'
import { BrandMark } from './BrandMark'

/**
 * Shared-password gate around the whole app. Renders a password
 * screen until the visitor enters the correct password (validated
 * server-side via /api/site-unlock); after that the cached key
 * unlocks every subsequent visit on this device.
 *
 * NOT used for /r/<uuid> report pages — those are public and the
 * router renders them before this gate is reached.
 */
export function SiteGate({ children }: { children: React.ReactNode }) {
  const [unlocked, setUnlocked] = useState<boolean>(() => getStoredSiteKey() !== null)

  if (unlocked) {
    return <>{children}</>
  }
  return <SiteLogin onUnlock={() => setUnlocked(true)} />
}

function SiteLogin({ onUnlock }: { onUnlock: () => void }) {
  const [value, setValue] = useState('')
  const [checking, setChecking] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    const key = value.trim()
    if (!key) return
    setChecking(true)
    setError(null)
    const ok = await validateSiteKey(key)
    setChecking(false)
    if (ok) {
      setStoredSiteKey(key)
      onUnlock()
    } else {
      setError('Incorrect password.')
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <form
        onSubmit={submit}
        className="w-full max-w-sm rounded-xl border border-massing-rule glass p-7"
      >
        <div className="flex items-center gap-3 mb-6">
          <BrandMark size={34} />
          <span className="brand-wordmark text-2xl brand-gradient-text leading-none">massing</span>
        </div>
        <div className="brand-eyebrow text-massing-ink-light mb-1.5">
          NSW residential feasibility
        </div>
        <h1 className="font-serif text-xl text-massing-ink mb-4 leading-snug">
          Enter the password to continue
        </h1>
        <input
          type="password"
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Password"
          autoComplete="current-password"
          className="w-full border border-massing-rule bg-massing-paper-deep text-massing-ink placeholder:text-massing-ink-light/70 rounded-sm px-3 py-2 text-sm focus:border-massing-highlight focus:outline-none"
        />
        {error && (
          <div className="text-[12.5px] text-flag-red mt-2">{error}</div>
        )}
        <button
          type="submit"
          disabled={checking || !value.trim()}
          className="mt-4 w-full bg-massing hover:bg-massing-deep text-massing-cream px-4 py-2 text-sm tracking-wide rounded-sm disabled:opacity-60"
        >
          {checking ? 'Checking…' : 'Unlock'}
        </button>
        <p className="text-[11px] text-massing-ink-light mt-4 leading-snug">
          Remembered on this device — you won't be asked again. Shared
          report links open without a password.
        </p>
      </form>
    </div>
  )
}
